import {
  ComplianceEntityAdvanceRequest,
  ComplianceEntityForm,
  ComplianceEntityTaxClassification,
  ComplianceEntityType,
  ComplianceEntityTaxIdentifierUpdate,
  ComplianceEntityUpdate
} from "@wingspanhq/wingspan-api-sdk";
import { FormDataBusinessInformation } from "../../../components/FormBusinessInformation";
import { FormDataPersonalInformation } from "../../../components/FormPersonalInformation";
import { FormDataUSInternationalPersonInformation } from "../../../components/FormUSInternationalPersonInformation";
import { phoneFormDataToE164Optional } from "../../../components/FormPartialPhoneNumber";

/**
 * The advance transition carries the whole entity, so it is the widest shape the
 * change flow builds. The update body accepts a subset — see {@link toEntityUpdate}.
 */
export type EntityChangeData = Omit<
  ComplianceEntityAdvanceRequest,
  "taxIdentifiers"
> & {
  // Narrower than the advance request's `TaxIdentifier`, whose value is optional:
  // the change flow only ever builds identifiers that carry a value, and the
  // update body requires one.
  taxIdentifiers?: ComplianceEntityTaxIdentifierUpdate[];
};

/**
 * Narrows an advance payload to the fields the update body accepts. A business
 * legal name, date of birth, entity form, tax classification and formation
 * jurisdiction have no field on the update, so a change to any of those only
 * lands through the advance transition.
 */
export function toEntityUpdate(data: EntityChangeData): ComplianceEntityUpdate {
  return {
    jurisdictionCountry: data.jurisdictionCountry,
    doingBusinessAs: data.doingBusinessAs,
    natureOfBusiness: data.natureOfBusiness,
    websiteUrl: data.websiteUrl,
    individualLegalName: data.individualLegalName,
    physicalAddress: data.physicalAddress,
    taxIdentifiers: data.taxIdentifiers,
    phoneNumber: data.phoneNumber,
    email: data.email,
    occupation: data.occupation
  };
}

/**
 * Tax identifiers are add-only: re-submitting a registration already on file is a
 * material change the update body rejects, so drop those.
 */
export function onlyNewTaxIdentifiers(
  taxIdentifiers: ComplianceEntityTaxIdentifierUpdate[] | undefined,
  existing: { countryCode?: string; type?: string; hasValue?: boolean }[] = []
): ComplianceEntityTaxIdentifierUpdate[] | undefined {
  const onFile = new Set(
    existing
      .filter(taxId => taxId.hasValue)
      .map(taxId => `${taxId.countryCode}:${taxId.type}`)
  );
  const fresh = (taxIdentifiers ?? []).filter(
    taxId => !onFile.has(`${taxId.countryCode}:${taxId.type}`)
  );

  return fresh.length > 0 ? fresh : undefined;
}

export function getIndividualEntityData(props: {
  personFormData: FormDataPersonalInformation;
  usPersonFormData?: FormDataUSInternationalPersonInformation;
  country: string;
}): EntityChangeData {
  const { person } = props.personFormData;
  const personCountry = person.country || props.country;
  const usPerson = props.usPersonFormData?.usPerson;

  const taxIdentifiers: ComplianceEntityTaxIdentifierUpdate[] = [];
  if (person.taxId) {
    taxIdentifiers.push({
      countryCode: personCountry,
      type: personCountry === "US" ? "SSN" : "TIN",
      value: person.taxId
    });
  }
  // A non-US individual who is also a US person carries a US SSN alongside their
  // local registration. The US-person names and "since" date have no v3 home.
  if (personCountry !== "US" && usPerson?.ssn) {
    taxIdentifiers.push({
      countryCode: "US",
      type: "SSN",
      value: usPerson.ssn
    });
  }

  return {
    type: ComplianceEntityType.INDIVIDUAL,
    jurisdictionCountry: personCountry,
    individualLegalName: {
      givenName: person.firstName || undefined,
      middleNames: person.middleName || undefined,
      familyName: person.lastName || undefined
    },
    dateOfBirth: person.birthday || undefined,
    physicalAddress: {
      line1: person.address.addressLine1 || undefined,
      line2: person.address.addressLine2 || undefined,
      city: person.address.city || undefined,
      state: person.address.state || undefined,
      postalCode: person.address.postalCode || undefined,
      country: personCountry
    },
    taxIdentifiers: taxIdentifiers.length > 0 ? taxIdentifiers : undefined,
    occupation: person.jobTitle || undefined,
    email: person.email || undefined,
    phoneNumber: phoneFormDataToE164Optional(person.phone)
  };
}

export function getBusinessEntityData(
  formData: FormDataBusinessInformation,
  country: string
): EntityChangeData {
  let form: ComplianceEntityForm | undefined;
  let taxClassification: ComplianceEntityTaxClassification | undefined;

  switch (formData.taxClassification) {
    case "SoleProprietorship":
      form = ComplianceEntityForm.SOLE_PROPRIETORSHIP;
      break;
    case "LlcSingleMember":
      form = ComplianceEntityForm.LIMITED_LIABILITY;
      taxClassification =
        ComplianceEntityTaxClassification.TAXED_AS_DISREGARDED_ENTITY;
      break;
    case "CorporationS":
      form = ComplianceEntityForm.CORPORATION;
      taxClassification =
        ComplianceEntityTaxClassification.TAXED_AS_S_CORPORATION;
      break;
    case "CorporationC":
      form = ComplianceEntityForm.CORPORATION;
      taxClassification =
        ComplianceEntityTaxClassification.TAXED_AS_C_CORPORATION;
      break;
    case "Partnership":
      form = ComplianceEntityForm.PARTNERSHIP;
      taxClassification =
        ComplianceEntityTaxClassification.TAXED_AS_PARTNERSHIP;
      break;
    case "LlcCorporationS":
      form = ComplianceEntityForm.LIMITED_LIABILITY;
      taxClassification =
        ComplianceEntityTaxClassification.TAXED_AS_S_CORPORATION;
      break;
    case "LlcCorporationC":
      form = ComplianceEntityForm.LIMITED_LIABILITY;
      taxClassification =
        ComplianceEntityTaxClassification.TAXED_AS_C_CORPORATION;
      break;
    case "LlcPartnership":
      form = ComplianceEntityForm.LIMITED_LIABILITY;
      taxClassification =
        ComplianceEntityTaxClassification.TAXED_AS_PARTNERSHIP;
      break;
    case "NotForProfitOrganization":
      form = ComplianceEntityForm.NON_PROFIT;
      break;
  }

  return {
    type: ComplianceEntityType.BUSINESS,
    jurisdictionCountry: country,
    form,
    taxClassification,
    legalName: formData.legalBusinessName || undefined,
    doingBusinessAs: formData.dba || undefined,
    natureOfBusiness: formData.industry || undefined,
    websiteUrl: formData.noWebsite ? undefined : formData.website || undefined,
    phoneNumber: phoneFormDataToE164Optional(formData.phone),
    physicalAddress: {
      line1: formData.address.addressLine1 || undefined,
      line2: formData.address.addressLine2 || undefined,
      city: formData.address.city || undefined,
      state: formData.address.state || undefined,
      postalCode: formData.address.postalCode || undefined,
      country
    },
    formationJurisdiction: formData.stateOfIncorporation
      ? { countryCode: country, subdivisionCode: formData.stateOfIncorporation }
      : undefined,
    taxIdentifiers: formData.taxId
      ? [{ countryCode: country, type: "EIN", value: formData.taxId }]
      : undefined
  };
}
